import React, { useState } from 'react';
import { Container, Typography, Box, Card, Grid, Avatar, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ariesImage from '../images/aries.png';
import taurusImage from '../images/taurus.png';
import geminiImage from '../images/gemini.png';
import cancerImage from '../images/cancer.png';
import leoImage from '../images/leo.png';
import virgoImage from '../images/virgo.png';
import libraImage from '../images/libra.png';
import scorpioImage from '../images/scorpio.png';
import sagittariusImage from '../images/sagitarius.png';
import capricornImage from '../images/capricon.png';
import aquariusImage from '../images/aquarius.png';
import piscesImage from '../images/pisces.png';

// Mapping zodiac to images
const zodiacImages = {
  Aries: ariesImage,
  Taurus: taurusImage,
  Gemini: geminiImage,
  Cancer: cancerImage,
  Leo: leoImage,
  Virgo: virgoImage,
  Libra: libraImage,
  Scorpio: scorpioImage,
  Sagittarius: sagittariusImage,
  Capricorn: capricornImage,
  Aquarius: aquariusImage,
  Pisces: piscesImage
};

// Zodiac characteristics
const zodiacCharacteristics = {
  Aries: { element: 'Fire', quality: 'Cardinal', dislikes: ['Routine'], likes: ['Adventure', 'Challenges'] },
  Taurus: { element: 'Earth', quality: 'Fixed', dislikes: ['Instability'], likes: ['Comfort', 'Stability'] },
  Gemini: { element: 'Air', quality: 'Mutable', dislikes: ['Monotony'], likes: ['Variety', 'Socializing'] },
  Cancer: { element: 'Water', quality: 'Cardinal', dislikes: ['Neglect'], likes: ['Security', 'Home'] },
  Leo: { element: 'Fire', quality: 'Fixed', dislikes: ['Being Ignored'], likes: ['Admiration', 'Creativity'] },
  Virgo: { element: 'Earth', quality: 'Mutable', dislikes: ['Disorganization'], likes: ['Order', 'Detail'] },
  Libra: { element: 'Air', quality: 'Cardinal', dislikes: ['Conflict'], likes: ['Harmony', 'Balance'] },
  Scorpio: { element: 'Water', quality: 'Fixed', dislikes: ['Betrayal'], likes: ['Intensity', 'Mystery'] },
  Sagittarius: { element: 'Fire', quality: 'Mutable', dislikes: ['Restriction'], likes: ['Freedom', 'Exploration'] },
  Capricorn: { element: 'Earth', quality: 'Cardinal', dislikes: ['Incompetence'], likes: ['Ambition', 'Structure'] },
  Aquarius: { element: 'Air', quality: 'Fixed', dislikes: ['Convention'], likes: ['Innovation', 'Independence'] },
  Pisces: { element: 'Water', quality: 'Mutable', dislikes: ['Harshness'], likes: ['Compassion', 'Imagination'] }
};

// Horoscope data
const horoscopeData = {
  Aries: {
    today: "Your confidence and ease with how you smile at everyone around you may trip everyone into romantic reveries of you. However, the chances of unfortunate incidents happening today cannot be ignored entirely. Today, your intellect and your composure in handling difficult situations will make you attractive. You have established your own ground, and your partner has been supportive of you from the start. A health-related issue or work crisis will pop up for either of you, and you will be able to handle it well. Your partner will be proud of how you have slid through the issue with a calm, wise, collected mind. While you gather appreciation, you may tend to go wayward in admiring yourself and that blots an ink mark on your personality. Be aware of your tendencies to be consumed in the commendation you have received.",
  },
  Taurus: {
    today: "Today is a day to focus on practical aspects of your relationship. The harmony you seek can be found by nurturing the stability and security you’ve built together. Your partner may appreciate your grounded approach, especially if you address any lingering issues with patience and understanding. Be mindful of not becoming too set in your ways, as flexibility could enhance your connection. An unexpected pleasant surprise or gift may strengthen your bond.",
  },
  Gemini: {
    today: "Your social charm will be at its peak today, drawing people to you with ease. In your romantic life, this could mean exciting conversations and deep connections. Be open to spontaneous experiences with your partner, as these moments could bring you closer together. Keep an eye on communication to avoid misunderstandings. Your ability to adapt to new situations will be particularly beneficial in your relationship.",
  },
  Cancer: {
    today: "Your emotional depth and intuition are strong today, making it an excellent time to connect with your partner on a deeper level. Share your feelings openly and encourage your partner to do the same. You may find comfort in reminiscing about shared memories or discussing future plans. Avoid letting minor insecurities affect your mood or your interactions. A nurturing attitude will strengthen your relationship.",
  },
  Leo: {
    today: "You are likely to shine brightly today, drawing admiration and affection from those around you. In your relationship, your confidence and charisma can enhance your bond. However, be cautious not to overshadow your partner’s feelings or needs. Balance your assertiveness with empathy and understanding. A fun outing or creative activity together could bring you even closer.",
  },
  Virgo: {
    today: "Your attention to detail and practicality are highlighted today, which can positively impact your relationship. Address any minor issues with your partner calmly and constructively. Your partner may appreciate your thoughtful approach to problem-solving. Take some time to focus on self-care and relaxation as well, as this will help you maintain balance in both your personal and romantic life.",
  },
  Libra: {
    today: "Today is a day for seeking balance and harmony in your relationship. Focus on creating a peaceful and loving environment with your partner. Your diplomatic skills will be useful in resolving any conflicts or misunderstandings. Make an effort to appreciate and acknowledge your partner’s efforts, as this will strengthen your bond. Engaging in activities that bring joy and satisfaction to both of you will be beneficial.",
  },
  Scorpio: {
    today: "Your intensity and passion are likely to be at the forefront today, making it a powerful time for deepening your connection with your partner. Share your thoughts and feelings openly, and encourage your partner to do the same. Be mindful of any tendencies toward jealousy or possessiveness, as these could create tension. Embrace vulnerability and authenticity to enhance your relationship.",
  },
  Sagittarius: {
    today: "Your adventurous spirit and enthusiasm will shine today, making it a great time for exciting experiences with your partner. Embrace spontaneity and explore new activities together. Your optimism and openness will inspire your partner and strengthen your connection. Keep communication clear to avoid misunderstandings and ensure that both of you feel heard and appreciated.",
  },
  Capricorn: {
    today: "Today is an excellent day for focusing on long-term goals and stability in your relationship. Your practical approach will help you address any ongoing issues and set the stage for future growth. Your partner will likely appreciate your dedication and commitment. Take some time to reflect on your relationship’s progress and discuss any future plans together. Balance ambition with affection.",
  },
  Aquarius: {
    today: "Your innovative and unconventional approach will bring fresh energy to your relationship today. Embrace opportunities for creative expression and new experiences with your partner. Be open to exploring unconventional ideas and solutions together. Your partner will likely appreciate your unique perspective and willingness to think outside the box. Avoid being too detached or aloof, and stay engaged in your interactions.",
  },
  Pisces: {
    today: "Your sensitivity and compassion will be heightened today, making it an ideal time to connect emotionally with your partner. Share your dreams and aspirations openly, and encourage your partner to do the same. Your intuitive understanding will help you navigate any emotional complexities. Focus on creating a nurturing and supportive environment, and avoid letting minor issues overshadow the positive aspects of your relationship.",
  },
};


// Function to calculate compatibility
const calculateCompatibility = (zodiac1, zodiac2) => {
  const data1 = zodiacCharacteristics[zodiac1];
  const data2 = zodiacCharacteristics[zodiac2];
  
  if (!data1 || !data2) return 0;

  let score = 0;

  // Element Compatibility
  if (data1.element === data2.element) score += 20;
  
  // Quality Compatibility
  if (data1.quality === data2.quality) score += 20;
  
  // Compatibility Likes
  const commonLikes = data1.likes.filter(like => data2.likes.includes(like));
  score += commonLikes.length * 10;
  
  // Compatibility Dislikes
  const commonDislikes = data1.dislikes.filter(dislike => data2.dislikes.includes(dislike));
  score += commonDislikes.length * 10;

  return Math.min(score, 100);
};

// Function to suggest compatible zodiacs
const suggestCompatibleZodiac = (zodiac) => {
  const compatibilityScores = {
    Aries: ['Leo', 'Sagittarius', 'Gemini'],
    Taurus: ['Virgo', 'Capricorn', 'Cancer'],
    Gemini: ['Libra', 'Aquarius', 'Aries'],
    Cancer: ['Scorpio', 'Pisces', 'Taurus'],
    Leo: ['Aries', 'Sagittarius', 'Libra'],
    Virgo: ['Taurus', 'Capricorn', 'Cancer'],
    Libra: ['Gemini', 'Aquarius', 'Leo'],
    Scorpio: ['Cancer', 'Pisces', 'Virgo'],
    Sagittarius: ['Aries', 'Leo', 'Aquarius'],
    Capricorn: ['Taurus', 'Virgo', 'Scorpio'],
    Aquarius: ['Gemini', 'Libra', 'Sagittarius'],
    Pisces: ['Cancer', 'Scorpio', 'Capricorn'],
  };
  return compatibilityScores[zodiac] || [];
};

// Sample statistics for each zodiac
const zodiacStatistics = {
  Aries: { compatibility: 80, test: 't-test' },
  Taurus: { compatibility: 70, test: 'chi-square' },
  Gemini: { compatibility: 85, test: 'ANOVA' },
  Cancer: { compatibility: 75, test: 'regression' },
  Leo: { compatibility: 80, test: 't-test' },
  Virgo: { compatibility: 70, test: 'chi-square' },
  Libra: { compatibility: 85, test: 'ANOVA' },
  Scorpio: { compatibility: 75, test: 'regression' },
  Sagittarius: { compatibility: 80, test: 't-test' },
  Capricorn: { compatibility: 70, test: 'chi-square' },
  Aquarius: { compatibility: 85, test: 'ANOVA' },
  Pisces: { compatibility: 75, test: 'regression' },
};

const ZodiakLove = () => {
  const [yourSign, setYourSign] = useState('');
  const [partnersSign, setPartnersSign] = useState('');
  const [result, setResult] = useState(null);

  const handleCalculateLove = () => {
    if (yourSign && partnersSign) {
      const yourHoroscope = horoscopeData[yourSign]?.today || 'No horoscope available.';
      const compatibility = calculateCompatibility(yourSign, partnersSign);

      setResult({
        yourSign,
        partnersSign,
        yourHoroscope,
        compatibility,
        description: compatibility > 75
          ? 'You two are highly compatible! You have many common interests and understand each other well.'
          : 'There might be some challenges. Differences between you may need attention.',
        suggestions: compatibility <= 50 ? suggestCompatibleZodiac(yourSign) : [],
        statistics: zodiacStatistics[yourSign] || { compatibility: 0, test: 'none' }
      });
    } else {
      alert('Please select both your sign and your partner\'s sign.');
    }
  };

  const yourSignImage = zodiacImages[yourSign];
  const partnersSignImage = zodiacImages[partnersSign];

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Love Compatibility Calculator
      </Typography>
      
      <Typography variant="h6" align="center" gutterBottom>
        Discover how your zodiac signs align in love and relationships. Get insights into your daily horoscope and compatibility.
      </Typography>
      
      <Grid container spacing={4} mb={4}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="your-sign-label" sx={{ fontSize: '1rem' }}>Select Your Zodiac Sign</InputLabel>
            <Select
              labelId="your-sign-label"
              value={yourSign}
              onChange={(e) => setYourSign(e.target.value)}
              label="Your Sign"
              sx={{ fontSize: '1rem' }}
            >
              <MenuItem value="">Select Your Sign</MenuItem>
              {Object.keys(zodiacImages).map(zodiac => (
                <MenuItem key={zodiac} value={zodiac}>
                  <img src={zodiacImages[zodiac]} alt={zodiac} style={{ width: 30, height: 30, marginRight: 8 }} />
                  {zodiac}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="partner-sign-label" sx={{ fontSize: '1rem' }}>Select Your Partner's Zodiac Sign</InputLabel>
            <Select
              labelId="partner-sign-label"
              value={partnersSign}
              onChange={(e) => setPartnersSign(e.target.value)}
              label="Partner's Sign"
              sx={{ fontSize: '1rem' }}
            >
              <MenuItem value="">Select Partner's Sign</MenuItem>
              {Object.keys(zodiacImages).map(zodiac => (
                <MenuItem key={zodiac} value={zodiac}>
                  <img src={zodiacImages[zodiac]} alt={zodiac} style={{ width: 30, height: 30, marginRight: 8 }} />
                  {zodiac}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={handleCalculateLove}
        sx={{ mb: 4, display: 'block', margin: 'auto' }}
      >
        Calculate Compatibility
      </Button>

      {result && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Compatibility Report for {new Date().toDateString()}
          </Typography>
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} sm={5}>
              <Card sx={{ p: 2, boxShadow: 3 }}>
                <Typography variant="h6">Your Zodiac Sign: {result.yourSign}</Typography>
                <Avatar
                  src={zodiacImages[result.yourSign]}
                  alt={result.yourSign}
                  sx={{ width: 100, height: 100, margin: 'auto', mt: 2 }}
                />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {result.yourHoroscope}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Card sx={{ p: 2, boxShadow: 3 }}>
                <Typography variant="h6">Partner's Zodiac Sign: {result.partnersSign}</Typography>
                <Avatar
                  src={zodiacImages[result.partnersSign]}
                  alt={result.partnersSign}
                  sx={{ width: 100, height: 100, margin: 'auto', mt: 2 }}
                />
                {/* Add horoscope for partner if available */}
              </Card>
            </Grid>
          </Grid>
          
          <Card sx={{ mt: 4, p: 2, textAlign: 'center', boxShadow: 3 }}>
            <Typography variant="h6">Compatibility Analysis</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <strong>Compatibility Score:</strong> {result.compatibility}%
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              {result.description}
            </Typography>
            {result.suggestions.length > 0 && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Suggested Compatible Signs:</strong> {result.suggestions.join(', ')}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Statistics:</strong> {result.statistics.compatibility}% ({result.statistics.test})
            </Typography>
          </Card>
        </Box>
      )}
    </Container>
  );
};

export default ZodiakLove;
